import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../store/authStore';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/ui/PageHeader';
import { feedbackApi, type CreateFeedbackRequest } from '../../api/feedbackApi';

export function FeedbackScreen() {
  const user = useAuthStore((s) => s.user);
  const userId = user?.id;
  const queryClient = useQueryClient();

  const [form, setForm] = useState<CreateFeedbackRequest>({
    subject: '',
    message: '',
    rating: undefined,
  });
  const [showHistory, setShowHistory] = useState(false);

  const { data: feedbackHistory = [], isLoading: historyLoading } = useQuery({
    queryKey: ['feedback', userId],
    queryFn: () => feedbackApi.getUserFeedback(userId!),
    enabled: !!userId,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateFeedbackRequest) => feedbackApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feedback', userId] });
      setForm({ subject: '', message: '', rating: undefined });
      setShowHistory(true);

      // Haptic feedback
      try {
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('success');
      } catch {}
    },
  });

  const handleSubmit = () => {
    if (!form.message.trim()) {
      try {
        window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred('error');
      } catch {}
      return;
    }

    createMutation.mutate({
      subject: form.subject?.trim() || undefined,
      message: form.message.trim(),
      rating: form.rating || undefined,
    });
  };

  const handleRatingClick = (rating: number) => {
    setForm((prev) => ({ ...prev, rating: prev.rating === rating ? undefined : rating }));
    try {
      window.Telegram?.WebApp?.HapticFeedback?.impactOccurred('light');
    } catch {}
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    borderRadius: '10px',
    border: '1px solid var(--separator)',
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontSize: '17px',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '13px',
    color: 'var(--text-hint)',
    marginBottom: '6px',
    fontWeight: 500,
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-4 space-y-4 pb-24">
      <PageHeader
        title="Обратная связь"
        subtitle="Сообщите об ошибке, предложите идею или оставьте отзыв"
      />

      {/* Форма */}
      <Card>
        <div className="space-y-4">
          {/* Заголовок (необязательное) */}
          <div>
            <label style={labelStyle}>Тема (необязательно)</label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))}
              placeholder="Краткое описание"
              style={inputStyle}
              maxLength={100}
            />
          </div>

          {/* Сообщение (обязательное) */}
          <div>
            <label style={labelStyle}>
              Сообщение <span style={{ color: 'var(--danger)' }}>*</span>
            </label>
            <textarea
              value={form.message}
              onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
              placeholder="Опишите проблему или предложение"
              rows={5}
              style={{ ...inputStyle, resize: 'vertical', minHeight: '120px' }}
              maxLength={1000}
            />
            <div className="text-xs mt-1" style={{ color: 'var(--text-hint)', textAlign: 'right' }}>
              {form.message.length}/1000
            </div>
          </div>

          {/* Оценка (необязательное) */}
          <div>
            <label style={labelStyle}>Оценка приложения (необязательно)</label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => handleRatingClick(star)}
                  className="text-3xl transition-transform active:scale-90"
                  style={{
                    color: form.rating && star <= form.rating ? 'var(--accent)' : 'var(--separator)',
                  }}
                >
                  {form.rating && star <= form.rating ? '★' : '☆'}
                </button>
              ))}
            </div>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={!form.message.trim() || createMutation.isPending}
            fullWidth
          >
            {createMutation.isPending ? 'Отправка...' : 'Отправить'}
          </Button>
        </div>
      </Card>

      {/* История обращений */}
      <Card>
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="w-full flex items-center justify-between"
          style={{ color: 'var(--text-primary)' }}
        >
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold">Мои обращения</span>
            {(feedbackHistory?.length ?? 0) > 0 && (
              <span
                className="text-xs px-2 py-1 rounded-full"
                style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-hint)' }}
              >
                {feedbackHistory?.length ?? 0}
              </span>
            )}
          </div>
          <span
            className="text-xl transition-transform"
            style={{
              transform: showHistory ? 'rotate(180deg)' : 'rotate(0deg)',
              color: 'var(--accent)',
            }}
          >
            ▼
          </span>
        </button>

        {showHistory && (
          <div className="mt-4 space-y-3">
            {historyLoading ? (
              <p className="text-center py-4" style={{ color: 'var(--text-hint)' }}>
                Загрузка...
              </p>
            ) : !feedbackHistory || feedbackHistory.length === 0 ? (
              <p className="text-center py-4" style={{ color: 'var(--text-hint)' }}>
                У вас пока нет обращений
              </p>
            ) : (
              feedbackHistory.map((feedback) => (
                <div
                  key={feedback.id}
                  className="p-3 rounded-lg"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    borderLeft: '3px solid var(--accent)',
                  }}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                      {feedback.subject || 'Без темы'}
                    </span>
                    {feedback.rating && (
                      <div className="flex items-center gap-1">
                        <span style={{ color: 'var(--accent)' }}>★</span>
                        <span className="text-sm" style={{ color: 'var(--text-hint)' }}>
                          {feedback.rating}
                        </span>
                      </div>
                    )}
                  </div>
                  <p
                    className="text-sm mb-2 line-clamp-2"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {feedback.message}
                  </p>
                  <span className="text-xs" style={{ color: 'var(--text-hint)' }}>
                    {formatDate(feedback.created_at)}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
